import { Helmet } from "react-helmet-async";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { CheckCircle, Shield, Users, Headphones, TrendingUp, BarChart3, Star, AlertTriangle, MapPin, CheckCircle2, Users2, BarChart, MessageSquare, FileText, Phone, Trophy, Sparkles, ArrowUpRight, Coins, ArrowLeftRight, Settings, Zap, Building2 } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { brandLogos } from "@/constants/brandLogos";
import { LANDING_COLORS } from "@/lib/landing-styles";
import { RevealOnScroll, FloatingOrbs } from "@/components/landing";

const Agency = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [api, setApi] = useState<any>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  // Auto-scroll effect for carousel
  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, 2000);
    return () => clearInterval(interval);
  }, [api]);


  return (
    <div className="min-h-screen font-['Inter']">
      <Helmet>
        <title>GMB Management Tool for Marketing Agencies | Fly Web Dashboard</title>
        <meta name="description" content="Manage your clients' Google Business Profiles with Fly's Web Dashboard. Profile optimization, content publishing, rank tracking, review management & more. All features, full control." />
        <meta name="keywords" content="GMB management tool for agencies, Google Business Profile management, local SEO platform, agency GMB tool, multi-client management, GBP dashboard, review management tool" />
        <link rel="canonical" href="https://fly-social.com/agency" />

        {/* Open Graph tags */}
        <meta property="og:title" content="GMB Management Tool for Marketing Agencies | Fly Web Dashboard" />
        <meta property="og:description" content="Manage your clients' Google Business Profiles with Fly's Web Dashboard. Profile optimization, content publishing, rank tracking, review management & more." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fly-social.com/agency" />
        <meta property="og:image" content="https://fly-social.com/lovable-uploads/5e360c99-b89f-455c-80a1-66fed5df1555.png" />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="GMB Management Tool for Marketing Agencies | Fly Web Dashboard" />
        <meta name="twitter:description" content="Manage your clients' Google Business Profiles with Fly's Web Dashboard. Profile optimization, content publishing, rank tracking, review management & more." />
        <meta name="twitter:image" content="https://fly-social.com/lovable-uploads/5e360c99-b89f-455c-80a1-66fed5df1555.png" />

        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Fly Web Dashboard for Agencies",
            "description": "Complete Local SEO management platform for marketing agencies to manage their clients' Google Business Profiles",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "provider": {
              "@type": "Organization",
              "name": "Fly - ZoomLocal Tech",
              "url": "https://fly-social.com"
            },
            "offers": {
              "@type": "Offer",
              "price": "9.99",
              "priceCurrency": "USD",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "9.99",
                "priceCurrency": "USD",
                "unitText": "per location per month"
              }
            },
            "featureList": [
              "Profile Optimization",
              "Content Publishing",
              "Rank Tracking",
              "Review Management",
              "Performance Analytics",
              "Multi-location Management"
            ]
          })}
        </script>
      </Helmet>
      <NavBar />
      
      {/* Hero Section */}
      <section
        className="pt-24 md:pt-32 pb-16 lg:pb-20 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f0fdf4 40%, #ecfeff 100%)' }}
      >
        <FloatingOrbs variant="hero" />

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-start gap-12 max-w-[1280px] mx-auto">
            {/* Left Column - Content */}
            <div className="lg:w-1/2">
              <RevealOnScroll>
                <span
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 mb-6 rounded-full text-sm font-semibold border"
                  style={{
                    background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
                    borderColor: `${LANDING_COLORS.primary}30`,
                    color: LANDING_COLORS.primary
                  }}
                >
                  <Building2 className="h-4 w-4" />
                  FOR MARKETING AGENCIES
                </span>

                <h1
                  className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-6"
                  style={{ color: LANDING_COLORS.text.primary }}
                >
                  Manage Client Profiles{' '}
                  <span style={{ color: LANDING_COLORS.primary }}>With Full Control</span>{' '}
                  One Dashboard
                </h1>
              </RevealOnScroll>
              
              <RevealOnScroll delay={1}>
              {/* Feature Boxes - 2x4 Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {/* Row 1 */}
                <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-lg hover:border-purple-200 transition-all duration-300 min-h-[80px] flex items-center">
                  <div className="flex items-center gap-4 w-full">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${LANDING_COLORS.primary}10` }}>
                      <Trophy className="h-5 w-5" style={{ color: LANDING_COLORS.primary }} />
                    </div>
                    <span className="text-base font-medium leading-snug" style={{ color: LANDING_COLORS.text.primary }}>Rank on Top 10</span>
                  </div>
                </div>
                
                <div className="bg-white border border-purple-100 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-purple-200 transition-all duration-300 min-h-[80px] flex items-center">
                  <div className="flex items-center gap-4 w-full">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Sparkles className="h-5 w-5 text-purple-600" />
                    </div>
                    <span className="text-base font-medium text-gray-800 leading-snug">Visible Increase in Engagement</span>
                  </div>
                </div>
                
                {/* Row 2 */}
                <div className="bg-white border border-purple-100 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-purple-200 transition-all duration-300 min-h-[80px] flex items-center">
                  <div className="flex items-center gap-4 w-full">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-5 w-5 text-purple-600" />
                    </div>
                    <span className="text-base font-medium text-gray-800 leading-snug">Growth in Local Footfall</span>
                  </div>
                </div>
                
                <div className="bg-white border border-purple-100 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-purple-200 transition-all duration-300 min-h-[80px] flex items-center">
                  <div className="flex items-center gap-4 w-full">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <ArrowUpRight className="h-5 w-5 text-purple-600" />
                    </div>
                    <span className="text-base font-medium text-gray-800 leading-snug">Boost Client Retention with Continuous Growth</span>
                  </div>
                </div>
                
                {/* Row 3 */}
                <div className="bg-white border border-purple-100 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-purple-200 transition-all duration-300 min-h-[80px] flex items-center">
                  <div className="flex items-center gap-4 w-full">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Coins className="h-5 w-5 text-purple-600" />
                    </div>
                    <span className="text-base font-medium text-gray-800 leading-snug">Unlock More Revenue from Your Existing Clients</span>
                  </div>
                </div>
                
                <div className="bg-white border border-purple-100 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-purple-200 transition-all duration-300 min-h-[80px] flex items-center">
                  <div className="flex items-center gap-4 w-full">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <ArrowLeftRight className="h-5 w-5 text-purple-600" />
                    </div>
                    <span className="text-base font-medium text-gray-800 leading-snug">Easily Upsell to Your Entire Client Base</span>
                  </div>
                </div>
                
                {/* Row 4 */}
                <div className="bg-white border border-purple-100 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-purple-200 transition-all duration-300 min-h-[80px] flex items-center">
                  <div className="flex items-center gap-4 w-full">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Settings className="h-5 w-5 text-purple-600" />
                    </div>
                    <span className="text-base font-medium text-gray-800 leading-snug">Run Your Entire Setup with Zero Additional Employees</span>
                  </div>
                </div>
                
                <div className="bg-white border border-purple-100 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-purple-200 transition-all duration-300 min-h-[80px] flex items-center">
                  <div className="flex items-center gap-4 w-full">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Zap className="h-5 w-5 text-purple-600" />
                    </div>
                    <span className="text-base font-medium text-gray-800 leading-snug">Already Managing Social Media? Why Not Supercharge it with GMB?</span>
                  </div>
                </div>
              </div>
              </RevealOnScroll>
            </div>

            {/* Right Column - Enhanced Visual Card */}
            <div className="lg:w-1/2">
              <RevealOnScroll delay={2} direction="right">
              <div className="text-center relative">
                {/* Enhanced Visual Card with decorative elements */}
                <div className="relative">
                  {/* Decorative lightning badge - top right */}
                  <div className="absolute -top-2 -right-2 z-10 bg-yellow-400 rounded-full p-2 shadow-lg">
                    <Zap className="h-4 w-4 text-black" />
                  </div>
                  
                  {/* Main visual card */}
                  <div className="bg-white rounded-3xl p-6 lg:p-7 shadow-xl border border-purple-100 mb-8 relative transform hover:scale-105 hover:shadow-2xl transition-all duration-300" style={{boxShadow: '0 20px 60px -10px rgba(124, 58, 237, 0.2)'}}>
                    <img 
                      src="/lovable-uploads/5e360c99-b89f-455c-80a1-66fed5df1555.png" 
                      alt="Add GMB Management service - Boost Retention and Generate 10x Revenue" 
                      className="w-full h-auto rounded-2xl"
                    />
                    
                    {/* Decorative analytics badge - bottom left */}
                    <div className="absolute -bottom-2 -left-2 bg-purple-600 rounded-full p-2 shadow-lg">
                      <BarChart className="h-4 w-4 text-white" />
                    </div>
                  </div>
                </div>
                
                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                  <a href="https://fly-social.com/fly-app" target="_blank" rel="noopener noreferrer">
                    <Button
                      className="bg-fly-purple hover:bg-fly-purple/90 text-white font-bold px-8 py-3 text-base rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    >
                      Start 14-Day Free Trial
                    </Button>
                  </a>
                  <Link to="/pricing">
                    <Button
                      variant="outline"
                      className="border-2 border-purple-600 text-purple-600 bg-white font-semibold px-8 py-3 text-base rounded-2xl hover:bg-purple-600 hover:text-white transition-all duration-300"
                    >
                      View Pricing
                    </Button>
                  </Link>
                </div>

                {/* Exclusive Text */}
                <div style={{ color: LANDING_COLORS.primary }} className="text-lg font-bold">
                  14 Days Free · No Credit Card Required
                </div>
              </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 bg-gradient-to-r from-purple-600 to-purple-700 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <Shield className="h-8 w-8 mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-1">Authorise Google API partner</h3>
              <p className="text-sm opacity-90">Secure Integration</p>
            </div>
            <div>
              <Users className="h-8 w-8 mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-1">Used by 1000+ Locations</h3>
              <p className="text-sm opacity-90">Trusted by businesses worldwide</p>
            </div>
            <div>
              <Headphones className="h-8 w-8 mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-1">24/7 Support</h3>
              <p className="text-sm opacity-90">Always here when you need us</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 via-white to-purple-50 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-32 h-32 bg-purple-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-yellow-200 rounded-full blur-2xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block bg-white/80 backdrop-blur-sm rounded-full px-6 py-2 mb-4 shadow-sm">
              <p className="text-sm font-medium text-primary uppercase tracking-wider">Trusted by</p>
            </div>
            <h3 className="text-3xl font-bold text-foreground mb-2">Top consumer brands across the globe</h3>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto rounded-full"></div>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20">
              <Carousel 
                opts={{
                  align: "start",
                  loop: true,
                  dragFree: true
                }} 
                setApi={setApi} 
                className="relative px-10"
              >
                <CarouselContent className="-ml-2 md:-ml-4">
                  {brandLogos.map((brand, index) => (
                    <CarouselItem key={index} className="pl-2 md:pl-4 basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/4 xl:basis-1/5 flex items-center justify-center">
                       <div className="h-36 sm:h-40 md:h-44 w-full flex items-center justify-center p-4 rounded-xl bg-white/80 hover:bg-white hover:shadow-md transition-all duration-300 transform hover:scale-105">
                         <img
                           src={brand.logo}
                           alt={`${brand.name} logo`}
                           className="w-full h-full object-contain"
                           style={{ maxWidth: '200px', maxHeight: '140px' }}
                         />
                       </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="left-0 bg-white/90 backdrop-blur-sm hover:bg-white shadow-lg border-0" />
                <CarouselNext className="right-0 bg-white/90 backdrop-blur-sm hover:bg-white shadow-lg border-0" />
              </Carousel>
            </div>
          </div>
        </div>
      </section>

      {/* Why Agencies Choose Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
            Why Top Agencies Choose <span className="text-purple-600">Fly's Web Dashboard</span>
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Full control over your clients' Local SEO. All features in one powerful dashboard.
          </p>
          
          <div className="grid lg:grid-cols-3 gap-6 mb-16">
            <Card className="p-6 text-center bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <div className="bg-purple-600 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">3x Higher Rankings</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Proven methodology that consistently delivers triple the ranking improvements for our agency partners.</p>
            </Card>
            <Card className="p-6 text-center bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <div className="bg-purple-600 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">45% More Leads</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Our optimized GMB strategies generate nearly 45% more qualified leads than traditional approaches.</p>
            </Card>
            <Card className="p-6 text-center bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <div className="bg-purple-600 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">AI Review Management</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Intelligent automation that responds to reviews professionally and maintains your reputation 24/7.</p>
            </Card>
            <Card className="p-6 text-center bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <div className="bg-purple-600 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users2 className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Citation Networks</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Comprehensive citation building across 10-15 authoritative directories and local listing sites.</p>
            </Card>
            <Card className="p-6 text-center bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <div className="bg-purple-600 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                <BarChart className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Client Management Dashboard</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Manage all your clients' profiles from one centralized dashboard with detailed reporting.</p>
            </Card>
            <Card className="p-6 text-center bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <div className="bg-purple-600 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">ROI-Focused Analytics</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Clear, actionable insights that demonstrate real business impact and return on investment.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* GMB Problems Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl lg:text-5xl font-bold text-center mb-6 text-gray-900">
            GMB Problems That <span className="text-purple-600">Kill Agency Relationships?</span> We Fix Them All.
          </h2>
          <p className="text-center text-gray-600 mb-16 max-w-3xl mx-auto text-lg">
            Stop losing clients to GMB issues. Our specialized team solves the problems that destroy agency relationships.
          </p>
          
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <Card className="p-6 text-center bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="h-8 w-8 text-white" />
              </div>
              <div className="flex-grow flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">GMB Suspension Removal</h3>
                  <div className="text-purple-600 font-bold text-lg mb-3">94% Success Rate</div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">Get suspended accounts reinstated quickly with our proven appeal process.</p>
              </div>
            </Card>
            
            <Card className="p-6 text-center bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Phone className="h-8 w-8 text-white" />
              </div>
              <div className="flex-grow flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">Phone Number Not Updating</h3>
                  <div className="text-blue-600 font-bold text-lg mb-3">Technical Experts</div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">Our specialists fix stubborn phone number issues that Google won't resolve.</p>
              </div>
            </Card>
            
            <Card className="p-6 text-center bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <MapPin className="h-8 w-8 text-white" />
              </div>
              <div className="flex-grow flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">
                    Get Location<br />
                    Verified
                  </h3>
                  <div className="text-purple-600 font-bold text-lg mb-3">Challenging Cases</div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">We handle the most difficult verification scenarios other services can't solve.</p>
              </div>
            </Card>
            
            <Card className="p-6 text-center bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-white" />
              </div>
              <div className="flex-grow flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">Perfect GMB Profile Setup</h3>
                  <div className="text-blue-600 font-bold text-lg mb-3">No Cost</div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">Complete optimization of your GMB profiles at no additional charge.</p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* 3 Steps Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
            From Crisis to <span className="text-purple-600">Success</span> in 3 Steps
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Our proven methodology transforms GMB challenges into competitive advantages for your agency.
          </p>
          
          <div className="flex flex-col md:flex-row items-center justify-center gap-12 mb-16">
            <div className="text-center max-w-xs">
              <div className="bg-purple-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">01</div>
              <div className="bg-purple-600 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Audit & Identify</h3>
              <p className="text-gray-600 text-sm">We perform a comprehensive audit of your current GMB setup and identify all issues affecting your client relationships.</p>
            </div>
            <div className="hidden md:block text-4xl text-gray-300">→</div>
            <div className="text-center max-w-xs">
              <div className="bg-yellow-500 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">02</div>
              <div className="bg-yellow-500 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Optimize & Automate</h3>
              <p className="text-gray-600 text-sm">Our platform automatically fixes common issues and sets up robust systems to prevent future problems.</p>
            </div>
            <div className="hidden md:block text-4xl text-gray-300">→</div>
            <div className="text-center max-w-xs">
              <div className="bg-green-500 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">03</div>
              <div className="bg-green-500 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Scale & Succeed</h3>
              <p className="text-gray-600 text-sm">Watch your client satisfaction soar as you deliver consistent, reliable GMB management with detailed reporting.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Five Core Features */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-6 text-gray-900">
            Five Core Features
          </h2>
          <p className="text-center text-gray-600 mb-16 max-w-3xl mx-auto text-lg">
            Everything you need to manage your Google My Business presence effectively and efficiently.
          </p>
          
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 max-w-7xl mx-auto">
            {/* Profile Management */}
            <Card className="p-6 text-center bg-white rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between h-full">
              <div>
                <div className="bg-purple-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <Users2 className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Profile Management</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">Complete control over your Google My Business profiles with automated updates and optimization.</p>
              </div>
              <Link to="/features/profile-management">
                <Button 
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg w-full mt-auto"
                >
                  Learn More
                </Button>
              </Link>
            </Card>
            
            {/* Multi-Location Management */}
            <Card className="p-6 text-center bg-white rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between h-full">
              <div>
                <div className="bg-purple-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <MapPin className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Multi-Location Management</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">Manage hundreds of locations from one dashboard with bulk editing and synchronized updates.</p>
              </div>
              <Link to="/features/multi-location-management">
                <Button 
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg w-full mt-auto"
                >
                  Learn More
                </Button>
              </Link>
            </Card>
            
            {/* Customer Insights */}
            <Card className="p-6 text-center bg-white rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between h-full">
              <div>
                <div className="bg-purple-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <BarChart3 className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Customer Insights</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">Deep analytics and customer behavior insights to help you make data-driven decisions.</p>
              </div>
              <Link to="/features/customer-insights">
                <Button 
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg w-full mt-auto"
                >
                  Learn More
                </Button>
              </Link>
            </Card>
            
            {/* Reputation Management */}
            <Card className="p-6 text-center bg-white rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between h-full">
              <div>
                <div className="bg-purple-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <MessageSquare className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Reputation Management</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">Automated review responses and reputation monitoring to maintain your online presence.</p>
              </div>
              <Link to="/features/reputation-management">
                <Button 
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg w-full mt-auto"
                >
                  Learn More
                </Button>
              </Link>
            </Card>
            
            {/* Content Management */}
            <Card className="p-6 text-center bg-white rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between h-full">
              <div>
                <div className="bg-purple-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <FileText className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Content Management</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">Streamlined content creation and posting across all your business locations.</p>
              </div>
              <Link to="/features/content-management">
                <Button 
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg w-full mt-auto"
                >
                  Learn More
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </section>

      {/* Lead Generation Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">Start Managing Client Profiles Today</h2>
          <p className="text-center text-purple-600 font-semibold mb-2">All features. Full control. One dashboard.</p>
          <p className="text-center text-gray-600 mb-12">Get a FREE GMB audit for any client and see how Fly can transform their Local SEO.</p>
          
          <div className="flex flex-col lg:flex-row items-start gap-12 max-w-6xl mx-auto">
            <div className="lg:w-1/2">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="h-12 text-base border-gray-300"
                  />
                  <Input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="h-12 text-base border-gray-300"
                  />
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="h-12 text-base border-gray-300"
                  />
                  <Input
                    type="text"
                    name="company"
                    placeholder="Agency/Company Name"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="h-12 text-base border-gray-300"
                  />
                  <Button type="submit" className="w-full bg-fly-purple hover:bg-fly-purple/90 text-white py-3 h-12 text-base font-bold rounded-lg">
                    Get My Free GMB Audit Now →
                  </Button>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Shield className="h-4 w-4 text-yellow-600" />
                    <span>Your information is secure and will only be used to send your GMB audit.</span>
                  </div>
                </form>
              </div>
            </div>
            
            <div className="lg:w-1/2">
              <div className="relative">
                <div className="bg-blue-50 rounded-lg p-6 mb-6 relative">
                  <div className="absolute top-4 right-4 w-3 h-3 bg-blue-500 rounded-full"></div>
                  <div className="absolute top-6 right-8 w-2 h-2 bg-blue-400 rounded-full"></div>
                  <div className="absolute bottom-4 right-6 w-2 h-2 bg-blue-300 rounded-full"></div>
                  <div className="absolute bottom-8 left-4 w-3 h-3 bg-blue-600 rounded-full"></div>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-blue-600 text-white rounded-lg p-2 w-10 h-10 flex items-center justify-center text-lg font-bold">
                      G
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Your Business Name</div>
                      <div className="text-sm text-gray-600">Business Category</div>
                      <div className="text-sm text-gray-600">📍 Your Location</div>
                    </div>
                  </div>
                  <div className="flex text-yellow-400 mb-2">
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                  </div>
                </div>
                
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="font-bold mb-2 text-gray-900">GMB Emergency? Call Now:</h3>
                  <div className="flex items-center gap-2 text-2xl font-bold mb-4 text-blue-600">
                    <Phone className="h-6 w-6" />
                    <a href="tel:+918446712833" className="hover:underline">+91 8446712833</a>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-gray-700">Immediate GMB problem diagnosis</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-gray-700">Free audit reveals hidden ranking issues</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Agency;